import {
  Button,
  Card,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow
} from "@material-ui/core";
import {
  createStyles,
  makeStyles,
  Theme,
  withStyles
} from "@material-ui/core/styles";
import { Delete, InfoOutlined } from "@material-ui/icons";
import CustomNumberInput from "@saleor/components/CustomNumberInput";
import React from "react";
import { FormattedMessage } from "react-intl";

import { LocationRateRowObject } from "./LocationRate";
import {
  LOCATION_RATE_DELIVERY_OPTIONS,
  validateLocationRateFee,
  validateLocationRateMax,
  validateLocationRateMin
} from "./utils";

const useStyles = makeStyles(
  {
    btnAddPrice: {
      "& span": {
        color: "#0030FF !important"
      },
      border: "2px solid #0030FF !important",
      borderRadius: "30px",
      fontSize: "small",
      height: "30px",
      marginLeft: "auto"
    },
    btnAddPriceDisabled: {
      opacity: "70%"
    },
    card: {
      overflow: "auto"
    },
    cardTitleBody: {
      alignItems: "center",
      display: "flex",
      fontSize: "17px"
    },
    cardTitleColored: {
      "& span": {
        color: "#002972",
        fontSize: "small",
        marginLeft: "5px"
      },
      backgroundColor: "#F4F8FF"
    },
    table: {
      minWidth: 700
    },
    tableHeader: {
      fontSize: 15,
      fontWeight: 600,
      verticalAlign: "bottom"
    }
  },
  { name: "LocationRateTableInput" }
);

const StyledTableCell = withStyles(
  (theme: Theme) =>
    createStyles({
      body: {
        fontSize: 12
      },
      head: {
        color: theme.palette.common.black
      }
    }),
  { name: "InputTable" }
)(TableCell);

const StyledTableRow = withStyles(
  (_: Theme) =>
    createStyles({
      root: {
        "&:nth-of-type(even)": {
          backgroundColor: "#F4F8FF"
        }
      }
    }),
  { name: "InputTable" }
)(TableRow);

interface LocationRateTableInputProps {
  defaultDelType: string;
  selectedDelType: string;
  defaultRows?: LocationRateRowObject[];
  rowHasError: boolean;
  rows: LocationRateRowObject[];
  setRows: React.Dispatch<React.SetStateAction<LocationRateRowObject[]>>;
}

export const LocationRateTableInput: React.FC<LocationRateTableInputProps> = props => {
  const {
    defaultDelType,
    selectedDelType,
    defaultRows,
    rowHasError,
    rows,
    setRows
  } = props;
  const classes = useStyles(props);

  const maxForDelType = parseFloat(
    LOCATION_RATE_DELIVERY_OPTIONS.find(d => d.value === selectedDelType)
      ?.max || "0.00"
  );
  const minForDelType = parseFloat(
    LOCATION_RATE_DELIVERY_OPTIONS.find(d => d.value === selectedDelType)
      ?.min || "0.00"
  );

  React.useEffect(() => {
    if (defaultRows && defaultRows.length > 0) {
      setRows(defaultRows);
    }
  }, [defaultRows]);

  React.useEffect(() => {
    if (
      selectedDelType &&
      selectedDelType.length > 0 &&
      (defaultRows.length === 0 || defaultDelType !== selectedDelType)
    ) {
      setRows([
        {
          disableMax: false,
          disableMin: true,
          disablePrice: false,
          feeError: "",
          hideDelete: true,
          index: 0,
          max: "0.00",
          maxError: "",
          min: selectedDelType ? minForDelType.toFixed(2) : "0.00",
          minError: "",
          price: "0.00"
        }
      ]);
    }
  }, [selectedDelType]);

  const handleAddPrice = (ctr: number) => {
    if (
      !validateLocationRateMax(
        rows[ctr - 1],
        setRows,
        maxForDelType,
        parseFloat(rows[ctr - 1].max).toFixed(2)
      )
    ) {
      return;
    }

    const latestMax = rows.length > 0 ? rows[rows.length - 1].max : "0.00";
    setRows(prevRows => [
      ...prevRows,
      {
        disableMax: false,
        disableMin: true,
        disablePrice: false,
        feeError: "",
        hideDelete: false,
        index: ctr,
        max: "0.00",
        maxError: "",
        min: latestMax,
        minError: "",
        price: "0.00"
      }
    ]);

    setRows(prevRows =>
      prevRows.map(r =>
        r === rows[rows.length - 1]
          ? {
              ...r,
              disableMax: true,
              disableMin: true,
              disablePrice: false,
              hideDelete: true
            }
          : r
      )
    );
  };

  const handleDeleteRow = index => {
    setRows(prevRows =>
      prevRows.slice(0, -1).map((r, i) => {
        if (i !== 0) {
          return i === index - 1
            ? { ...r, disableMax: false, hideDelete: false }
            : r;
        } else if (i === 0) {
          return i === index - 1
            ? { ...r, disableMax: false, disableMin: !!selectedDelType }
            : r;
        }

        return r;
      })
    );
  };

  return (
    <Card className={classes.card}>
      <Table
        className={classes.table}
        aria-label="customized location rate table with input"
        id="shippingLocationRateTable"
      >
        <TableHead>
          <TableRow>
            <StyledTableCell colSpan={4} className={classes.cardTitleColored}>
              <div className={classes.cardTitleBody}>
                <InfoOutlined fontSize="inherit" />
                <span>
                  The entered distance will always be rounded to two decimal
                  places.
                </span>
                <Button
                  id="shippingLocationRateAddPriceBtn"
                  onClick={() => handleAddPrice(rows.length)}
                  className={
                    !!rowHasError
                      ? `${classes.btnAddPrice} ${classes.btnAddPriceDisabled}`
                      : classes.btnAddPrice
                  }
                  variant="outlined"
                  disabled={!!rowHasError}
                >
                  <FormattedMessage
                    defaultMessage="Add Distance and Pricing +"
                    description="button"
                  />
                </Button>
              </div>
            </StyledTableCell>
          </TableRow>
          <TableRow>
            <StyledTableCell className={classes.tableHeader}>
              Minimum Distance
            </StyledTableCell>
            <StyledTableCell className={classes.tableHeader}>
              Maximum Distance
            </StyledTableCell>
            <StyledTableCell className={classes.tableHeader}>
              Delivery Price
            </StyledTableCell>
            <StyledTableCell className={classes.tableHeader}></StyledTableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, index) => (
            <StyledTableRow key={"shippingLocationRateTableInputRow" + index}>
              <StyledTableCell component="th" scope="row">
                <CustomNumberInput
                  id={"shippingLocationRateMinRow" + index}
                  value={row.min}
                  label={"kM."}
                  disabled={row.disableMin}
                  onChange={e => {
                    const newValue = e.target.value;
                    setRows(prevRows =>
                      prevRows.map(r =>
                        r === row ? { ...r, min: newValue } : r
                      )
                    );
                  }}
                  error={!!row.minError}
                  errorText={row.minError}
                  onBlur={e => {
                    validateLocationRateMin(
                      row,
                      setRows,
                      parseFloat(e.target.value).toFixed(2)
                    );
                  }}
                />
              </StyledTableCell>
              <StyledTableCell>
                <CustomNumberInput
                  id={"shippingLocationRateMaxRow" + index}
                  value={row.max}
                  label={"kM."}
                  disabled={row.disableMax}
                  onChange={e => {
                    const newValue = e.target.value;
                    setRows(prevRows =>
                      prevRows.map(r =>
                        r === row ? { ...r, max: newValue } : r
                      )
                    );
                  }}
                  error={!!row.maxError}
                  errorText={row.maxError}
                  onBlur={e => {
                    validateLocationRateMax(
                      row,
                      setRows,
                      maxForDelType,
                      parseFloat(e.target.value).toFixed(2)
                    );
                  }}
                />
              </StyledTableCell>
              <StyledTableCell>
                <CustomNumberInput
                  id={"shippingLocationRatePriceRow" + index}
                  value={row.price}
                  label={"PHP"}
                  disabled={row.disablePrice}
                  onChange={e => {
                    const newValue = e.target.value;
                    setRows(prevRows =>
                      prevRows.map(r =>
                        r === row ? { ...r, price: newValue } : r
                      )
                    );
                  }}
                  error={!!row.feeError}
                  errorText={row.feeError}
                  onBlur={e => {
                    validateLocationRateFee(
                      row,
                      setRows,
                      parseFloat(e.target.value).toFixed(2)
                    );
                  }}
                />
              </StyledTableCell>
              <StyledTableCell>
                {!row.hideDelete && (
                  <Delete
                    id={"shippingLocationRateDeleteRow" + index}
                    color="error"
                    onClick={() => handleDeleteRow(index)}
                  />
                )}
              </StyledTableCell>
            </StyledTableRow>
          ))}
        </TableBody>
      </Table>
    </Card>
  );
};

LocationRateTableInput.displayName = "LocationRateTableInput";
export default LocationRateTableInput;
